import { Box, Divider, Typography } from '@mui/material';
import useAppTheme from 'hooks/useAppTheme';
import { useAppSelector } from 'store/hook';
import {
  calculateWorkOrderStats,
  formatNumberToShortScale,
} from '../utils/dataFormatters';

import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  Label,
  Tooltip,
} from 'recharts';
import { useMemo, useState } from 'react';
import { HighestCostingCard } from './HighestCostingCard';
import { getImageNameByMakeModelAndAsset } from 'utils/helpers/mapAssetImage';
import { Selector } from 'components/Form/Selectors/Selector';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

interface PieChartDataItem {
  name: string;
  value: number;
  color: string;
}

const CustomHalfPieChart = ({
  data,
  label,
  title,
}: {
  data: PieChartDataItem[];
  label: string | null;
  title: string;
}) => {
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  return (
    <Box display={'flex'} flexDirection={'column'} gap={'4px'}>
      {title && (
        <Typography
          sx={{
            color: palette.grey,
          }}
          fontSize={'12px'}
        >
          {title}
        </Typography>
      )}
      <Box display={'flex'} gap={2} width={'100%'} justifyContent={'center'}>
        <div style={{ width: '180px', height: '80px' }}>
          <ResponsiveContainer>
            <PieChart>
              <Pie
                data={data}
                dataKey="value"
                startAngle={180}
                endAngle={0}
                cx="50%"
                cy="95%"
                innerRadius={50}
                outerRadius={70}
                paddingAngle={2}
                style={{
                  outline: 'none',
                }}
              >
                {data.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={entry.color} />
                ))}
                {label !== null && label !== undefined && (
                  <Label
                    position="center"
                    content={() => (
                      <text
                        x="50%"
                        y="65%"
                        fill={palette.textPrimary}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={16}
                        fontWeight="bold"
                      >
                        <tspan>{label}</tspan>
                      </text>
                    )}
                  />
                )}
              </Pie>
              <Tooltip
                contentStyle={{
                  backgroundColor: enableDarkTheme
                    ? palette.toolBarBackground
                    : palette.boxBackground,
                  border: enableDarkTheme
                    ? '1px solid #2F445D'
                    : '1px solid #BDBDBD',
                  borderRadius: '4px',
                  color: palette.textPrimary,
                }}
                labelStyle={{
                  color: palette.textPrimary,
                  fontSize: '14px',
                  fontWeight: 600,
                }}
                itemStyle={{
                  color: palette.textPrimary,
                  fontSize: '12px',
                }}
              />
            </PieChart>
          </ResponsiveContainer>
        </div>
        <Box
          display="flex"
          flexDirection="column"
          gap={'4px'}
          justifyContent={'center'}
          alignItems={'start'}
        >
          {data.map((entry, index) => (
            <Box
              key={`legend-${index}`}
              display="flex"
              alignItems="center"
              gap={1}
            >
              <Box
                width={8}
                height={8}
                bgcolor={entry.color}
                borderRadius="50%"
              />
              <Box
                display={'flex'}
                flexDirection={'row'}
                alignItems={'center'}
                gap={'2px'}
                color={palette.textPrimary}
              >
                <Typography variant="body2" fontSize={12}>
                  {entry.name}
                </Typography>

                <Typography variant="body1" fontSize={12} fontWeight="bold">
                  {`(${formatNumberToShortScale(entry.value, 0)})`}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export const WorkOrders = () => {
  const { t } = useTranslation();
  const chartView = [
    { display: t('status_distribution'), id: 'status_distribution' },
    { display: t('action_performed'), id: 'action_performed' },
    { display: t('pm_vs_cm_count'), id: 'pm_vs_cm_count' },
  ];
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;

  const selectedRangeWorkOrderData = useAppSelector(
    (state) => state.squaresPartsMaintenanceExpenditureReducer.data?.currentData
  );
  const assetsData = useAppSelector((state) => state.assetReducer.assets);

  const [filterOption, setFilterOption] = useState('status_distribution');

  const stats = useMemo(() => {
    const temp = calculateWorkOrderStats(selectedRangeWorkOrderData);
    if (!temp.topAssetByWorkOrders) {
      return temp;
    }
    const assetWithHighestWorkOrderInfo = assetsData.find(
      (el) => el.bumperNumber === temp.topAssetByWorkOrders?.bumper_number
    );
    return {
      ...temp,
      topAssetByWorkOrders: {
        ...temp.topAssetByWorkOrders,
        icon:
          assetWithHighestWorkOrderInfo?.make &&
          assetWithHighestWorkOrderInfo?.model
            ? getImageNameByMakeModelAndAsset(
                assetWithHighestWorkOrderInfo?.make,
                assetWithHighestWorkOrderInfo?.model,
                assetWithHighestWorkOrderInfo?.assetType,
                'Small'
              )
            : null,
        makeModel: `${assetWithHighestWorkOrderInfo?.make}-${assetWithHighestWorkOrderInfo?.model}`,
      },
    };
  }, [selectedRangeWorkOrderData]);

  const pmVsCmCountPieChartData = [
    { name: 'PM', value: stats.pmCount, color: '#FFA403' },
    { name: 'CM', value: stats.cmCount, color: '#8198FD' },
  ];

  let statusPieChartData = [
    {
      name: t('open'),
      value: stats.openCount,
      color: '#2ED58B',
    },
    {
      name: t('closed'),
      value: stats.closedCount,
      color: '#8198FD',
    },
  ];

  if (stats?.backlogCount > 0) {
    statusPieChartData = [
      ...statusPieChartData,
      {
        name: 'Backlog',
        value: stats.backlogCount,
        color: '#FF0068',
      },
    ];
  }

  const actionPerformedPieChartData = [
    {
      name: 'Replace',
      value: stats.replaceCount,
      color: '#EB5757',
    },
    {
      name: 'Repair',
      value: stats.repairCount,
      color: '#FFA403',
    },
    {
      name: 'Other',
      value: stats.otherActionCount,
      color: '#C800FF',
    },
  ];

  return (
    <Box
      sx={{
        backgroundColor: enableDarkTheme
          ? palette.toolBarBackground
          : palette.toolBarBackground,
        borderRadius: '5px',
        border: enableDarkTheme ? '1px solid #2F445D' : '1px solid #BDBDBD',
      }}
      p={1}
      display={'flex'}
      flexDirection={'column'}
      gap={2}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
        }}
      >
        <Typography
          sx={{
            fontSize: '14px',
            color: palette.textPrimary,
            fontWeight: 'bolder',
          }}
        >
          {t('work_orders')}
        </Typography>
        <Selector
          value={filterOption}
          selectorOptions={chartView}
          onChange={(e) => {
            setFilterOption(e.target.value as string);
          }}
          enableDarkTheme={enableDarkTheme}
          minWidth="150px"
        />
      </Box>

      <Box display={'flex'} flexDirection={'column'} gap={2}>
        {filterOption === 'status_distribution' && (
          <CustomHalfPieChart
            data={statusPieChartData}
            label={`${formatNumberToShortScale(
              stats.openCount + stats.closedCount,
              0
            )}`}
            title=""
          />
        )}

        {filterOption === 'action_performed' && (
          <CustomHalfPieChart
            data={actionPerformedPieChartData}
            label={`${formatNumberToShortScale(
              stats.repairCount + stats.replaceCount + stats.otherActionCount,
              0
            )}`}
            title=""
          />
        )}

        {filterOption === 'pm_vs_cm_count' && (
          <CustomHalfPieChart
            data={pmVsCmCountPieChartData}
            label={`${formatNumberToShortScale(
              stats.pmCount + stats.cmCount,
              0
            )}`}
            title=""
          />
        )}
      </Box>
      <Divider
        orientation="vertical"
        flexItem
        sx={{
          height: '2px',
          background: enableDarkTheme ? '#2F445D' : '#9E9E9E',
        }}
      />
      {stats?.topAssetByWorkOrders && (
        <HighestCostingCard
          title={t('frequently_maintained_asset')}
          name={stats?.topAssetByWorkOrders?.bumper_number || ''}
          subName={
            'makeModel' in stats.topAssetByWorkOrders
              ? (stats.topAssetByWorkOrders as { makeModel: string }).makeModel
              : null
          }
          value={`${stats.topAssetByWorkOrders.count} Times`}
          icon={
            'icon' in stats.topAssetByWorkOrders
              ? (stats.topAssetByWorkOrders as { icon: string | null }).icon
              : null
          }
          usePngComponent={true}
        />
      )}
    </Box>
  );
};
